import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AccessLevel } from '../../../shared/schemas';
import { apiFetch } from '../lib/api';
import { formatNumber } from '../lib/format';
import { dict, useT } from '../lib/lang';
import { Icon } from './icons';
import { Button, cx, ErrorBox, Spinner } from './ui';

/** Chuỗi của màn hình chọn server — xem `lib/lang.tsx` về vì sao đặt tại chỗ. */
const T = dict({
  en: {
    title: 'Pick a server to manage',
    introFirst:
      'Each server is its own community with its own data. You can switch at any time from the left sidebar.',
    introSwitch: 'Switch to another community. Every open page reloads against the new server.',
    filterPlaceholder: 'Search by server name…',
    filterAria: 'Search servers',
    levelAdmin: 'Administrator',
    levelMod: 'Moderator',
    levelUnknown: 'permission unknown',
    members: (count: string) => `${count} members`,
    notConnected: '⚠ The bot cannot reach this server — figures will be empty.',
    currentBadge: 'open now',
    noMatch: (query: string) => `No server matches “${query}”.`,
    noGuilds:
      'Your Discord account has no admin or moderator access on any server this bot is in yet.',
    back: 'Back',
    loadingGuilds: 'Loading the server list…',
    ownerTitle: 'Bot owner console',
    ownerHint:
      'Everything that looks across ALL communities: overview, process logs, system-wide backup, the defaults every server starts from.',
    ownerCta: 'Open →',
  },
  vi: {
    title: 'Chọn server để quản trị',
    introFirst:
      'Mỗi server là một cộng đồng riêng với dữ liệu riêng. Chọn xong vẫn đổi được bất cứ lúc nào ở thanh bên trái.',
    introSwitch: 'Đổi sang một cộng đồng khác. Mọi trang đang mở sẽ nạp lại theo server mới.',
    filterPlaceholder: 'Tìm theo tên server…',
    filterAria: 'Tìm server',
    levelAdmin: 'Quản trị viên',
    levelMod: 'Kiểm duyệt viên',
    levelUnknown: 'không rõ quyền',
    members: (count: string) => `${count} thành viên`,
    notConnected: '⚠ Bot chưa vào được server này — số liệu sẽ trống.',
    currentBadge: 'đang mở',
    noMatch: (query: string) => `Không có server nào khớp “${query}”.`,
    noGuilds:
      'Tài khoản Discord của bạn chưa có quyền quản trị hay kiểm duyệt ở server nào bot đang tham gia.',
    back: 'Quay lại',
    loadingGuilds: 'Đang lấy danh sách server…',
    ownerTitle: 'Khu chủ bot',
    ownerHint:
      'Mọi thứ nhìn ngang qua TẤT CẢ cộng đồng: tổng quan, nhật ký tiến trình, sao lưu toàn hệ thống, và mặc định chung mà mọi server bắt đầu từ đó.',
    ownerCta: 'Mở →',
  },
});

/**
 * Màn hình chọn server, hiện ngay sau khi đăng nhập.
 *
 * ── Vì sao cần một màn hình riêng ──────────────────────────────────────────
 *
 * Panel phục vụ nhiều cộng đồng của những bên khác nhau, và MỌI trang đều thuộc
 * về đúng một server. Bản trước tự chọn giùm — server đầu tiên trong danh sách —
 * rồi để một ô `<select>` nhỏ ở góc thanh bên làm chỗ đổi. Hai chuyện hỏng vì đó:
 *
 *  1. **Không ai biết mình đang ở đâu.** Người quản trị hai cộng đồng đăng nhập
 *     và thấy ngay một bảng số liệu, không có bước nào nói đó là số liệu của ai.
 *     Với những trang ghi (trao thưởng, reset XP) thì nhầm server là nhầm người
 *     thật.
 *  2. **Ô chọn ẩn khi chỉ có một mục.** Đúng cho người một server, nhưng nó cũng
 *     là chỗ duy nhất nói ra tên server — nên với họ panel không bao giờ nói nó
 *     đang phục vụ cộng đồng nào.
 *
 * ── Người chỉ có MỘT server thì không phải chọn ────────────────────────────
 *
 * Bắt họ bấm qua một màn hình một-lựa-chọn mỗi lần đăng nhập là thu phí một cú
 * bấm để đổi lấy một thông tin họ đã biết. `Layout` tự chốt giúp — xem ở đó.
 */

export interface GuildOption {
  readonly id: string;
  readonly name: string;
  readonly isAnchor: boolean;
  readonly connected: boolean;
  readonly memberCount: number;
  readonly iconUrl: string | null;
  /** Cấp quyền của CHÍNH người đang đăng nhập ở server này. */
  readonly level: AccessLevel | null;
}

/** Nhãn cấp quyền — nhận `t` vì bảng chuỗi chỉ đọc được trong thân component. */
function levelLabel(t: typeof T.en, level: AccessLevel): string {
  return level === 'admin' ? t.levelAdmin : t.levelMod;
}

/** Chữ cái đầu, dùng khi server chưa đặt ảnh đại diện. */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return (words[0] ?? '').slice(0, 2).toUpperCase();
  return `${(words[0] ?? '')[0] ?? ''}${(words[1] ?? '')[0] ?? ''}`.toUpperCase();
}

/**
 * Cửa vào khu chủ bot — dùng chung cho màn hình Đổi server và trang chủ, hai
 * chỗ mà mỗi bản trước tự vẽ riêng và đã lệch nhau (một bản có dòng mô tả, bản
 * kia thì không). `owner-*` chứ không phải `warn-*`: hai ý nghĩa khác nhau vô
 * tình trùng một token — xem `index.css` mục về `--owner`.
 */
export function OwnerDoor({ onClick }: { onClick: () => void }): React.JSX.Element {
  const t = useT(T);
  return (
    <button
      type="button"
      onClick={onClick}
      className="mb-4 flex w-full items-center gap-3 rounded-xl border border-owner-edge bg-owner-soft p-4 text-left transition hover:border-owner hover:bg-owner-soft"
    >
      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-owner-soft text-owner">
        <Icon name="crown" size={22} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-medium text-fg">{t.ownerTitle}</span>
        <span className="mt-0.5 block text-xs text-fg-muted">{t.ownerHint}</span>
      </span>
      <span className="shrink-0 text-xs font-medium text-owner">{t.ownerCta}</span>
    </button>
  );
}

export function GuildChooser({
  guilds,
  current,
  onPick,
  onOwner,
  onCancel,
}: {
  guilds: readonly GuildOption[];
  /** Server đang mở, để đánh dấu. `''` = lần chọn đầu sau khi đăng nhập. */
  current: string;
  onPick: (guildId: string) => void;
  /**
   * Đi thẳng vào khu chủ bot. Bỏ trống = tài khoản này không phải chủ bot.
   *
   * ── Vì sao lối vào đó nằm ở ĐÂY, trên cùng ────────────────────────────────
   *
   * Màn hình này hỏi "bạn muốn quản trị cộng đồng nào", và với chủ bot thì có
   * một câu trả lời thứ ba mà nó chưa bao giờ nhận: *không cộng đồng nào cả —
   * tôi muốn nhìn ngang qua tất cả.* Trước lượt này, đường duy nhất tới đó là
   * chọn bừa một server, đợi cả panel nạp theo server đó, rồi cuộn xuống cuối
   * thanh bên tìm nhóm "Chủ bot" — ba bước cho một việc không liên quan gì tới
   * server vừa chọn.
   *
   * Nó đứng TRƯỚC danh sách server, không lẫn vào lưới: nó không phải một lựa
   * chọn cùng loại. Mọi thẻ bên dưới đưa bạn vào panel CỦA MỘT cộng đồng; thẻ
   * này đưa bạn ra khỏi phạm vi đó.
   */
  onOwner?: () => void;
  /** Có thì hiện nút quay lại — chỉ khi đang ĐỔI server, không phải lần chọn đầu. */
  onCancel?: () => void;
}): React.JSX.Element {
  const t = useT(T);

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <div className="w-full max-w-3xl">
        <header className="mb-6 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">{t.title}</h1>
          <p className="mt-1 text-sm text-fg-subtle">
            {current === '' ? t.introFirst : t.introSwitch}
          </p>
        </header>

        {/*
          Khu chủ bot — ĐỨNG TRƯỚC ô lọc, vì ô lọc tìm theo tên SERVER và thẻ này
          không phải một server. Lọt vào dưới ô lọc thì gõ một chữ là nó biến mất
          cùng những thẻ không khớp, và không có gì giải thích vì sao.
        */}
        {onOwner !== undefined && <OwnerDoor onClick={onOwner} />}

        {/*
          Ô lọc và dòng "không khớp" giờ nằm TRONG `GuildGrid` — xem chú thích ở
          đó. Rỗng vì bộ lọc (`shown.length === 0` với `guilds.length > 0`) đã tự
          xử lý bên trong; ở đây chỉ còn phải nói trường hợp danh sách GỐC rỗng.
        */}
        <GuildGrid guilds={guilds} current={current} onPick={onPick} />

        {guilds.length === 0 && (
          <p className="py-8 text-center text-sm text-fg-subtle">{t.noGuilds}</p>
        )}

        {onCancel !== undefined && (
          <div className="mt-6 text-center">
            <Button variant="ghost" onClick={onCancel}>
              {t.back}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Lưới thẻ server. Ba chỗ gọi: màn hình Đổi server, trang chủ (thẻ "Tiếp tục"
 * đã tách khỏi lưới), và khu chủ bot đọc `current` để biết ô nào đang mở.
 *
 * Tách ra thay vì chép: hai bản chép là hai bản sẽ lệch ở lần đổi màu tiếp theo,
 * mà đây lại là khối người dùng nhìn đầu tiên mỗi phiên làm việc.
 *
 * Ô lọc và dòng "không khớp" nằm NGAY TRONG component này, không phải ở nơi
 * gọi — trước 16/08/2026 mỗi nơi gọi tự giữ state lọc, ngưỡng `> 6`, và ba cặp
 * chuỗi song ngữ của riêng nó, và chúng đã lệch nhau. Trạng thái "không có
 * server nào" (khác với "không khớp bộ lọc") vẫn ở lại từng nơi gọi vì hai
 * trường hợp đó cần hai cách trả lời khác nhau thật sự.
 */
export function GuildGrid({
  guilds,
  current,
  onPick,
}: {
  guilds: readonly GuildOption[];
  /** Server đang mở, để đánh dấu. `''` = chưa chọn cái nào. */
  current: string;
  onPick: (guildId: string) => void;
}): React.JSX.Element {
  const t = useT(T);
  const [filter, setFilter] = useState('');

  const shown = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (needle.length === 0) return guilds;
    return guilds.filter((guild) => guild.name.toLowerCase().includes(needle));
  }, [guilds, filter]);

  return (
    <>
      {/* Ô lọc chỉ xuất hiện khi danh sách đủ dài để cần tới nó. */}
      {guilds.length > 6 && (
        <input
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          placeholder={t.filterPlaceholder}
          aria-label={t.filterAria}
          className="mb-4 w-full rounded-lg border border-border-strong bg-surface px-3 py-2 text-sm placeholder:text-fg-subtle focus:border-accent focus:outline-none"
        />
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        {shown.map((guild) => {
          const isCurrent = guild.id === current;
          return (
            <button
              key={guild.id}
              type="button"
              onClick={() => onPick(guild.id)}
              className={cx(
                'flex items-center gap-3 rounded-xl border p-4 text-left transition',
                'hover:border-accent hover:bg-accent-soft',
                isCurrent
                  ? 'border-accent bg-accent-soft'
                  : 'border-border bg-surface',
              )}
            >
              {guild.iconUrl !== null ? (
                <img src={guild.iconUrl} alt="" className="h-12 w-12 shrink-0 rounded-full" />
              ) : (
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-surface-3 text-sm font-semibold text-fg-muted">
                  {initials(guild.name)}
                </span>
              )}

              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium">{guild.name}</span>
                <span className="mt-0.5 block text-xs text-fg-subtle">
                  {guild.level === null ? t.levelUnknown : levelLabel(t, guild.level)}
                  {guild.connected && guild.memberCount > 0 && (
                    <> · {t.members(formatNumber(guild.memberCount))}</>
                  )}
                </span>
                {/*
                  Server cấu hình sai vẫn hiện, kèm lý do. Giấu nó đi là biến
                  một lỗi cấu hình thành sự im lặng không ai điều tra — và
                  người quản trị cộng đồng đó sẽ tưởng mình mất quyền.
                */}
                {!guild.connected && (
                  <span className="mt-1 block text-xs text-warn">
                    {t.notConnected}
                  </span>
                )}
              </span>

              {isCurrent && (
                <span className="shrink-0 text-xs font-medium text-accent">
                  {t.currentBadge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {guilds.length > 0 && shown.length === 0 && (
        <p className="py-8 text-center text-sm text-fg-subtle">{t.noMatch(filter)}</p>
      )}
    </>
  );
}

/**
 * Thẻ "Tiếp tục với X" ở đầu trang chủ — câu trả lời cho chính câu hỏi trang đó
 * tồn tại để hỏi, nên tách khỏi lưới thay vì làm một ô trong đó. Tên server
 * KHÔNG lặp lại trong thẻ: nó đã là tiêu đề `h1` ngay phía trên.
 *
 * `shortcuts` do nơi gọi truyền vào (đọc từ `HOME_SHORTCUTS` ở `lib/nav.ts`) —
 * component này không biết panel có những trang nào, chỉ biết cách vẽ nút.
 */
export function GuildHero({
  guild,
  current,
  shortcuts,
  onOpen,
}: {
  guild: GuildOption;
  /** Server đang mở, để quyết định có vẽ huy hiệu "đang mở" hay không. */
  current: string;
  shortcuts: readonly { to: string; label: string }[];
  onOpen: (guildId: string, to: string) => void;
}): React.JSX.Element {
  const t = useT(T);
  const isCurrent = guild.id === current;

  return (
    <section className="mb-5 flex flex-wrap items-center gap-3 rounded-2xl border border-accent-edge bg-accent-soft p-4 sm:p-5">
      {guild.iconUrl !== null ? (
        <img src={guild.iconUrl} alt="" className="h-12 w-12 shrink-0 rounded-full" />
      ) : (
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-surface text-sm font-semibold text-fg-muted">
          {initials(guild.name)}
        </span>
      )}

      {/* Vắng mặt ở lần đăng nhập đầu (server duy nhất tự đôn lên): chưa mở gì cả. */}
      {isCurrent && (
        <span className="shrink-0 text-xs font-medium text-accent">{t.currentBadge}</span>
      )}

      <div className="flex w-full flex-wrap gap-2 sm:w-auto sm:flex-1 sm:justify-end">
        {shortcuts.map((item, index) => (
          <Button
            key={item.to}
            variant={index === 0 ? 'primary' : 'secondary'}
            onClick={() => onOpen(guild.id, item.to)}
          >
            {item.label}
          </Button>
        ))}
      </div>

      {/* Không bao giờ giấu — lý do ở chú thích `notConnected` trong `GuildGrid`. */}
      {!guild.connected && <p className="w-full text-xs text-warn">{t.notConnected}</p>}
    </section>
  );
}

/**
 * Nạp danh sách server một lần cho cả panel.
 *
 * Dùng chung `queryKey` với mọi chỗ khác đọc `/api/guilds` để hai nơi không bao
 * giờ hiện hai danh sách khác nhau.
 */
export function useGuilds(): {
  guilds: GuildOption[];
  loading: boolean;
  error: unknown;
} {
  const { data, isLoading, error } = useQuery({
    queryKey: ['guilds'],
    queryFn: () => apiFetch<{ guilds: GuildOption[] }>('/api/guilds'),
    // Danh sách server đổi khi bot được mời vào chỗ mới hoặc quyền bị thu hồi —
    // hiếm, nhưng không nên đứng yên cả phiên đăng nhập.
    staleTime: 5 * 60_000,
  });

  return { guilds: data?.guilds ?? [], loading: isLoading, error };
}

/** Màn hình chờ khi đang nạp danh sách server. */
export function GuildChooserLoading({ error }: { error?: unknown }): React.JSX.Element {
  const t = useT(T);
  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      {error === undefined || error === null ? (
        <Spinner label={t.loadingGuilds} />
      ) : (
        <div className="w-full max-w-md">
          <ErrorBox error={error} />
        </div>
      )}
    </div>
  );
}
